import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { SERVICES, type RequestDto, type RequestStatus, REQUEST_STATUSES } from '@shared';
import { apiGet } from '../../lib/api';
import { StatusChip, statusLabel } from '../../components/StatusChip';

type Filter = 'all' | RequestStatus;

export function MyRequests() {
  const [rows, setRows] = useState<RequestDto[] | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiGet<{ requests: RequestDto[] }>('/api/requests')
      .then((r) => setRows(r.requests))
      .catch((err: Error) => setError(err.message ?? 'Failed to load requests.'));
  }, []);

  const list = useMemo(
    () => (rows ?? []).filter((r) => filter === 'all' ? true : r.status === filter),
    [rows, filter],
  );

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <h1 className="portal-h1">My briefs</h1>
          <p className="portal-sub">All briefs you’ve submitted to Omega — current and historical.</p>
        </div>
        <Link className="btn accent" to="/portal/requests/new">+ Submit a new brief</Link>
      </div>

      <div className="tabs" style={{ marginTop: 24 }}>
        {(['all', ...REQUEST_STATUSES] as Filter[]).map((t) => (
          <button key={t} className={filter === t ? 'on' : ''} onClick={() => setFilter(t)}>
            {t === 'all' ? 'All' : statusLabel(t)}
          </button>
        ))}
      </div>

      <div className="card flush">
        <table className="tbl">
          <thead>
            <tr>
              <th>Ref</th><th>Engagement</th><th>Service</th><th>Status</th><th>Submitted</th>
            </tr>
          </thead>
          <tbody>
            {error && (
              <tr><td colSpan={5} style={{ color: 'var(--red)', fontSize: 13 }}>{error}</td></tr>
            )}
            {rows !== null && !error && list.length === 0 && (
              <tr>
                <td colSpan={5} style={{ color: 'var(--fg-mute)', fontSize: 13, textAlign: 'center', padding: 32 }}>
                  No briefs yet. <Link to="/portal/requests/new" style={{ color: 'var(--accent)' }}>Submit your first →</Link>
                </td>
              </tr>
            )}
            {list.map((r) => {
              const svc = SERVICES.find((s) => s.id === r.service);
              return (
                <tr key={r.id}>
                  <td><Link to={`/portal/requests/${r.id}`}><span className="rid">{r.id}</span></Link></td>
                  <td>{r.headline}</td>
                  <td style={{ color: 'var(--fg-soft)' }}>{svc?.name ?? r.service}</td>
                  <td><StatusChip status={r.status} /></td>
                  <td style={{ color: 'var(--fg-mute)', fontSize: 12, fontFamily: 'var(--mono)' }}>
                    {r.created_at.slice(0, 10)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}
